import escape from 'lodash/escape';
import plainTextToEditorHtml from '../../../js/tiptap/plainTextToEditorHtml.js';
import { embedHostAllowed, embedSrcFor, singlePastedUrl } from '../../../js/utils/richTextEmbeds.js';
import { pageAnchorFor } from '../addOnAnchorFromSelection';
import { ADD_ON_BODY_TEXT_LIMIT, ADD_ON_FIELD_LIMITS, emptyAddOnDraft, recordFromDraft } from '../addOnDraft';
import type { NewAddOnRecord } from '../addOnDraft';
import { ADD_ON_DRAFT } from '../addOnModel';
import { inferTypeFrom } from '../addOnType';
import { insertAsFor, mapHeader, typeFor } from './batchColumns';
import type { BatchColumn, ColumnMapping, InsertAs } from './batchColumns';
import { isBlankRow } from './spreadsheet';
import type { Grid } from './spreadsheet';

/** Real batches run to 700 pieces. A sheet far past that is more likely the wrong file. */
export const MAX_BATCH_ROWS = 1000;

type LimitedField = keyof typeof ADD_ON_FIELD_LIMITS;

export type RowError =
    | { code: 'titleMissing' }
    | { code: 'pageMissing' }
    | { code: 'pageInvalid' }
    | { code: 'pageOutOfRange'; total: number }
    | { code: 'pageUnplaceable' }
    | { code: 'tooLong'; field: 'content'; max: number }
    | { code: 'contentEmptied' }
    | { code: 'duplicateExisting' }
    | { code: 'duplicateRow'; line: number };

export type RowWarning =
    | { code: 'embedFellBackToLink' }
    | { code: 'imageUrlInvalid' }
    | { code: 'imageUnreachable' }
    | { code: 'insertAsUnknown' }
    | { code: 'truncated'; field: LimitedField; max: number }
    | { code: 'typeUnknown' };

export interface BatchEntry {
    /** The sheet's own row number, so the operator finds the row in their file. */
    line: number;
    record: NewAddOnRecord;
}

export interface PlannedRow {
    line: number;
    cells: Partial<Record<BatchColumn, string>>;
    errors: RowError[];
    warnings: RowWarning[];
    /** How a content cell holding only a URL went in, or null for any other content. */
    insertedAs: InsertAs | null;
    entry: BatchEntry | null;
}

export interface BatchPlan {
    fatal: 'empty' | 'missingColumns' | 'tooManyRows' | null;
    mapping: ColumnMapping;
    rows: PlannedRow[];
    entries: BatchEntry[];
}

export interface BatchContext {
    /** The publication's page count, or null while it is unknown: the host checks again. */
    totalPages: number | null;
    embedHosts: readonly string[];
    existing: ReadonlyArray<{ title?: unknown; anchor?: { page?: unknown } | null }>;
    /** The chapter an EPUB page lives in, which a page anchor needs there. Null when the book has none. */
    hrefForPage?: (page: number) => string | null;
    /** Whether the host's sanitizer would leave nothing of a markup body. Without it, the host is the one to say. */
    emptiesBody?: (html: string) => boolean;
    newUuid: () => string;
}

/** Checks every row against the book and the pieces already on it. A fatal plan carries no rows. */
export function planBatch(grid: Grid, context: BatchContext): BatchPlan {
    const headerIndex = grid.findIndex(row => !isBlankRow(row));
    const mapping = mapHeader(grid[headerIndex] ?? []);
    const body = grid
        .map((cells, index) => ({ cells, line: index + 1 }))
        .slice(headerIndex + 1)
        .filter(({ cells }) => !isBlankRow(cells));

    const plan = (fatal: BatchPlan['fatal'], rows: PlannedRow[] = []): BatchPlan => ({
        fatal,
        mapping,
        rows,
        entries: rows.flatMap(row => (row.entry ? [row.entry] : [])),
    });

    if (headerIndex < 0 || body.length === 0) return plan('empty');
    if (mapping.missing.length > 0) return plan('missingColumns');
    if (body.length > MAX_BATCH_ROWS) return plan('tooManyRows');

    const existing = new Set(
        context.existing.map(addOn => duplicateKey(String(addOn.title ?? ''), Number(addOn.anchor?.page)))
    );
    const claimedBy = new Map<string, number>();

    return plan(
        null,
        body.map(({ cells, line }) => planRow(cellsOf(cells, mapping), line, context, existing, claimedBy))
    );
}

function cellsOf(cells: readonly string[], mapping: ColumnMapping): Partial<Record<BatchColumn, string>> {
    const read: Partial<Record<BatchColumn, string>> = {};
    mapping.byIndex.forEach((column, index) => {
        if (column) read[column] = (cells[index] ?? '').trim();
    });
    return read;
}

/** Only a row the batch creates claims its title and page, so a skipped row never hides a later one. */
function planRow(
    sheetCells: Partial<Record<BatchColumn, string>>,
    line: number,
    context: BatchContext,
    existing: Set<string>,
    claimedBy: Map<string, number>
): PlannedRow {
    const warnings: RowWarning[] = [];
    const errors: RowError[] = [];
    const cells = withinLimits(sheetCells, warnings);

    const title = cells.title ?? '';
    if (!title) errors.push({ code: 'titleMissing' });

    const page = pageFrom(cells.page ?? '', context, errors);
    let href: string | undefined;
    if (page != null && context.hrefForPage) {
        href = context.hrefForPage(page) ?? undefined;
        if (!href) errors.push({ code: 'pageUnplaceable' });
    }

    const insertAs = cells.insertAs ? insertAsFor(cells.insertAs) : null;
    if (cells.insertAs && !insertAs) warnings.push({ code: 'insertAsUnknown' });
    const { body, insertedAs } = bodyFrom(cells.content ?? '', insertAs, context.embedHosts, warnings);
    if (cells.content?.startsWith('<') && context.emptiesBody?.(body)) errors.push({ code: 'contentEmptied' });
    if (textLength(body) > ADD_ON_BODY_TEXT_LIMIT) {
        errors.push({ code: 'tooLong', field: 'content', max: ADD_ON_BODY_TEXT_LIMIT });
    }

    const imageUrl = imageUrlFrom(cells.imageUrl ?? '', warnings);
    const sheetType = cells.type ? typeFor(cells.type) : null;
    if (cells.type && !sheetType) warnings.push({ code: 'typeUnknown' });

    const key = page != null ? duplicateKey(title, page) : null;
    if (title && key && existing.has(key)) {
        errors.push({ code: 'duplicateExisting' });
    } else if (title && key && claimedBy.has(key)) {
        errors.push({ code: 'duplicateRow', line: claimedBy.get(key)! });
    }

    const row: PlannedRow = { line, cells, errors, warnings, insertedAs, entry: null };
    if (errors.length > 0 || page == null || !key) return row;

    claimedBy.set(key, line);
    const form = {
        ...emptyAddOnDraft({ anchor: pageAnchorFor({ page, href }) }),
        title,
        body,
        pretitle: cells.pretitle ?? '',
        author: cells.author ?? '',
        byLine: cells.byLine ?? '',
        imageUrl: imageUrl ?? '',
        imageCaption: cells.imageCaption ?? '',
    };
    const type = sheetType ?? inferTypeFrom({ body, imageUrl: imageUrl ?? '' });
    const record = recordFromDraft(form, null, type, { status: ADD_ON_DRAFT });

    return { ...row, entry: { line, record: { ...record, uuid: context.newUuid() } } };
}

/** The editor's fields stop at the host's limits, so a longer cell is cut there too, and says so. */
function withinLimits(
    cells: Partial<Record<BatchColumn, string>>,
    warnings: RowWarning[]
): Partial<Record<BatchColumn, string>> {
    const kept = { ...cells };
    (Object.entries(ADD_ON_FIELD_LIMITS) as [LimitedField, number][]).forEach(([field, max]) => {
        const value = kept[field];
        if (!value || value.length <= max) return;

        // Not half of a character the cut would split in two.
        kept[field] = value
            .slice(0, max)
            .replace(/[\uD800-\uDBFF]$/, '')
            .trimEnd();
        warnings.push({ code: 'truncated', field, max });
    });

    return kept;
}

/**
 * Whole numbers only: "12" or the "12.0" a numeric cell can export. A range or a roman numeral names no single page,
 * and three zeros after the point read as a thousands separator ("1.000"), so they name no page either.
 */
function pageFrom(cell: string, context: BatchContext, errors: RowError[]): number | null {
    if (!cell) {
        errors.push({ code: 'pageMissing' });
        return null;
    }
    if (!/^\d+(?:\.0{1,2})?$/.test(cell) || parseInt(cell, 10) < 1) {
        errors.push({ code: 'pageInvalid' });
        return null;
    }

    const page = parseInt(cell, 10);
    if (context.totalPages && page > context.totalPages) {
        errors.push({ code: 'pageOutOfRange', total: context.totalPages });
        return null;
    }

    return page;
}

/**
 * The content cell holds prose, a link, or an embed code, and the reader
 * receives one HTML body. Markup goes as it is, since the host sanitizes
 * every body the way it sanitizes the editor's.
 */
function bodyFrom(
    content: string,
    insertAs: InsertAs | null,
    hosts: readonly string[],
    warnings: RowWarning[]
): { body: string; insertedAs: InsertAs | null } {
    if (!content) return { body: '', insertedAs: null };
    if (content.startsWith('<')) return { body: content, insertedAs: null };

    const url = singlePastedUrl(content) ?? (/^http:\/\/\S+$/i.test(content) ? content : null);
    if (url) {
        // The editor's paste rule: an allowed host whose page URL turns into a player.
        const src = insertAs !== 'link' && embedHostAllowed(url, [...hosts]) ? embedSrcFor(url) : null;
        if (src) return { body: `<iframe src="${escape(src)}" title="${escape(url)}"></iframe>`, insertedAs: 'embed' };

        // Asked to embed a host the platform does not frame: the reader still gets to it.
        if (insertAs === 'embed') warnings.push({ code: 'embedFellBackToLink' });
        return { body: `<p><a href="${escape(url)}">${escape(url)}</a></p>`, insertedAs: 'link' };
    }

    // The editor's own conversion, so a change in how it keeps prose reaches the batch too.
    const paragraphs = content
        .split(/\r?\n+/)
        .map(paragraph => paragraph.trim())
        .filter(Boolean);

    return { body: plainTextToEditorHtml(paragraphs.join('\n')), insertedAs: null };
}

function imageUrlFrom(cell: string, warnings: RowWarning[]): string | null {
    if (!cell) return null;
    if (/^https?:\/\/\S+$/i.test(cell)) return cell;

    warnings.push({ code: 'imageUrlInvalid' });
    return null;
}

/** Case, spacing and accents do not make a title another piece. */
function duplicateKey(title: string, page: number): string {
    const bare = title
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

    return `${page}\u0000${bare}`;
}

const textLength = (html: string) => html.replace(/<[^>]*>/g, '').length;
